import React, { useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Badge, FleetDiagram, VersionTimeline } from '@teals/ui-web';
import '@xyflow/react/dist/style.css';
import type { FleetNode, FleetEdge } from '@teals/ui-web';
import type { Agent, Session } from '../../store/api';
import { env } from '../../utils/env';

const TYPE_LABELS: Record<string, string> = {
  management: 'Management',
  conversational: 'Conversational',
  task: 'Task',
  general: 'General',
  data: 'Data',
};

/* ---------- layout ---------- */

const TwoPanel = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const PanelLeft = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid var(--border);

  @media (max-width: 1024px) {
    border-right: none;
    border-bottom: 1px solid var(--border);
  }
`;

const PanelRight = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--bg-surface);
  flex-shrink: 0;
`;

const PanelScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

/* ---------- sections ---------- */

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

/* ---------- stats ---------- */

const StatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 1rem;
  text-align: center;
`;

const StatCardValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const StatCardLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

/* ---------- metadata ---------- */

const OverviewCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;

  &:last-child {
    border-bottom: none;
  }
`;

const MetaKey = styled.span`
  color: var(--text-muted);
  font-weight: 500;
`;

const MetaVal = styled.span`
  color: var(--text-primary);
  font-weight: 500;
`;

const MonoVal = styled(MetaVal)`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
`;

/* ---------- capabilities ---------- */

const CapList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const CapChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 500;
  background: var(--accent-subtle);
  color: var(--accent);
`;

/* ---------- knowledge ---------- */

const KnowledgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const KnowledgeCount = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

/* ---------- tags ---------- */

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

/* ---------- Integration (collapsible) ---------- */

const IntegrationCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const IntegrationHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem 1.25rem;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
  border: none;
  background: none;
  font-family: inherit;
  text-align: left;

  &:hover {
    background: var(--bg-elevated);
  }
`;

const IntegrationSummary = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const IntegrationChevron = styled.span<{ $expanded: boolean }>`
  font-size: 0.75rem;
  color: var(--text-muted);
  transition: transform 0.2s;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(90deg)' : 'rotate(0)')};
`;

const IntegrationBody = styled.div`
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border);
  font-size: 0.8125rem;
`;

const IntegrationSubTitle = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
  margin-top: 1rem;

  &:first-child {
    margin-top: 0;
  }
`;

const EndpointRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 1rem 1.25rem;
`;

const MethodBadge = styled.span`
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  background: var(--success-subtle, #dcfce7);
  color: var(--success, #22c55e);
  flex-shrink: 0;
`;

const EndpointUrl = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  word-break: break-all;
  flex: 1;
`;

const CopyBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-subtle);
  }
`;

const AuthRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const AuthItem = styled.div`
  flex: 1;
  min-width: 180px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.875rem 1rem;
`;

const AuthLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.375rem;
`;

const AuthValueRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const AuthValue = styled.div`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
  color: var(--text-primary);
  word-break: break-all;
  flex: 1;
  min-width: 0;
`;

const ApiFieldNote = styled.p`
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin: 0.75rem 0 0;
  line-height: 1.5;
`;

const PayloadSection = styled.div`
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const PayloadHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: background 0.15s;
  background: none;
  font-family: inherit;
  text-align: left;

  &:hover {
    background: var(--bg-surface);
  }
`;

const PayloadTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const PayloadChevron = styled.span<{ $open: boolean }>`
  font-size: 0.75rem;
  color: var(--text-muted);
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? 'rotate(90deg)' : 'rotate(0)')};
`;

const PayloadBody = styled.pre<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  margin: 0;
  padding: 1rem 1.25rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
  line-height: 1.6;
  color: var(--text-secondary);
  white-space: pre-wrap;
  overflow-x: auto;
`;

/* ---------- component ---------- */

interface OverviewTabProps {
  agent: Agent;
  sessions: Session[];
}

function prettifyAgentName(name: string): string {
  return name
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function buildFleetFromAgentList(
  agent: Agent,
  agentNames: string[],
): { nodes: FleetNode[]; edges: FleetEdge[] } {
  const displayName = agent.displayName ?? agent.agentName;
  const count = agentNames.length;
  const cols = Math.min(count, 3);
  const colSpacing = 220;
  const rowSpacing = 100;
  const totalWidth = cols * colSpacing;
  const mgrX = totalWidth / 2 - 60;

  const nodes: FleetNode[] = [
    {
      id: 'mgr',
      type: 'managementAgent',
      data: { label: displayName, role: 'MANAGEMENT' },
      position: { x: mgrX, y: 0 },
    },
  ];

  const edges: FleetEdge[] = [];
  const colors: Array<'info' | 'warning'> = ['info', 'warning'];

  agentNames.forEach((name, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = col * colSpacing;
    const y = 110 + row * rowSpacing;
    nodes.push({
      id: name,
      type: 'subAgent',
      data: {
        label: prettifyAgentName(name),
        role: 'TASK',
        status: 'active',
        color: colors[i % 2],
      },
      position: { x, y },
    });
    edges.push({
      id: `e-mgr-${name}`,
      source: 'mgr',
      target: name,
      type: 'delegation',
      animated: true,
    });
  });

  return { nodes, edges };
}

function computeAvgResponseTime(sessions: Session[]): string {
  if (sessions.length === 0) return '—';
  const completed = sessions.filter(
    (s) => s.status === 'success' || s.status === 'failed',
  );
  if (completed.length === 0) return '—';
  const totalMs = completed.reduce((sum, s) => sum + (s.totalDurationMs ?? 0), 0);
  const avgSec = totalMs / completed.length / 1000;
  return avgSec < 1 ? `${(avgSec * 1000).toFixed(0)}ms` : `${avgSec.toFixed(1)}s`;
}

function getPublishedVersion(agent: Agent): string {
  const versions = agent.config?.versions;
  if (versions) {
    const pub = versions.find((v) => v.status === 'published');
    if (pub) return pub.version;
  }
  return agent.status === 'published' ? 'v1.0' : 'Draft';
}

const CHAT_ENDPOINT = `${env.API_ENDPOINT.replace(/\/$/, '')}/chat`;

const AUTH_HEADER = 'Authorization: Bearer <JWT_TOKEN>';
const API_KEY_HEADER = (key: string) =>
  key ? `x-api-key: ${key}` : 'x-api-key: <API_KEY>';

function buildRequestPayload(agentId: string): string {
  return JSON.stringify(
    {
      agent_id: agentId,
      message: 'Your message here',
      session_id: 'optional-existing-session-id',
      context: { channel: 'web', locale: 'en-US' },
      attachments: [],
      stream: false,
    },
    null,
    2,
  );
}

function buildResponsePayload(agentName: string): string {
  return JSON.stringify(
    {
      session_id: 'sess_xxx',
      message: { role: 'assistant', content: '...', agent: agentName },
      delegations: [],
      usage: { prompt_tokens: 0, completion_tokens: 0, latency_ms: 0 },
      version: 'v1.0',
    },
    null,
    2,
  );
}

function hasPublishedVersion(agent: Agent): boolean {
  if (agent.status === 'published') return true;
  const versions = agent.config?.versions;
  return Boolean(versions?.some((v) => v.status === 'published'));
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ agent, sessions }) => {
  const [integrationExpanded, setIntegrationExpanded] = useState(false);
  const [requestPayloadOpen, setRequestPayloadOpen] = useState(false);
  const [responsePayloadOpen, setResponsePayloadOpen] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const copyToClipboard = useCallback(
    async (text: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopyFeedback(label);
        setTimeout(() => setCopyFeedback(null), 1500);
      } catch {
        // ignore
      }
    },
    [],
  );

  const successRate =
    sessions.length > 0
      ? `${((sessions.filter((s) => s.status === 'success').length / sessions.length) * 100).toFixed(0)}%`
      : '—';

  const { fleetNodes, fleetEdges } = useMemo(() => {
    const fleet = agent.config?.fleet;

    if (process.env.NODE_ENV !== 'production') {
      console.debug('[OverviewTab] agent.config:', JSON.stringify(agent.config, null, 2));
      console.debug('[OverviewTab] fleet raw:', fleet, 'isArray:', Array.isArray(fleet));
    }

    if (!fleet) {
      console.warn('[OverviewTab] No fleet data found in agent.config');
      const fallback = buildFleetFromAgentList(agent, []);
      return { fleetNodes: fallback.nodes, fleetEdges: fallback.edges };
    }
    if (Array.isArray(fleet)) {
      console.info(`[OverviewTab] Building fleet from ${fleet.length} agent names`);
      const built = buildFleetFromAgentList(agent, fleet);
      return { fleetNodes: built.nodes, fleetEdges: built.edges };
    }
    if ('nodes' in fleet && Array.isArray(fleet.nodes)) {
      console.info(`[OverviewTab] Using structured fleet: ${fleet.nodes.length} nodes, ${fleet.edges?.length ?? 0} edges`);
      return { fleetNodes: fleet.nodes as FleetNode[], fleetEdges: (fleet.edges ?? []) as FleetEdge[] };
    }
    console.warn('[OverviewTab] Unrecognised fleet format:', fleet);
    const fallback = buildFleetFromAgentList(agent, []);
    return { fleetNodes: fallback.nodes, fleetEdges: fallback.edges };
  }, [agent]);

  const versions = agent.config?.versions ?? [
    {
      version: agent.status === 'published' ? 'v1.0' : 'Draft',
      status: (agent.status === 'published' ? 'published' : 'draft') as 'published' | 'draft',
      date: new Date(agent.updatedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      description: agent.status === 'published' ? 'Current production' : 'Working draft',
    },
    {
      version: 'Created',
      status: 'draft' as const,
      date: new Date(agent.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    },
  ];

  const knowledge = agent.config?.knowledge;

  return (
    <TwoPanel data-testid="overview-tab">
      <PanelLeft>
        <PanelHeader>Agent Overview</PanelHeader>
        <PanelScroll>
          <StatsStrip data-testid="stats-strip">
            <StatCard>
              <StatCardValue>{sessions.length}</StatCardValue>
              <StatCardLabel>Total Sessions</StatCardLabel>
            </StatCard>
            <StatCard>
              <StatCardValue>{computeAvgResponseTime(sessions)}</StatCardValue>
              <StatCardLabel>Avg Response Time</StatCardLabel>
            </StatCard>
            <StatCard>
              <StatCardValue>{successRate}</StatCardValue>
              <StatCardLabel>Success Rate</StatCardLabel>
            </StatCard>
            <StatCard>
              <StatCardValue>{getPublishedVersion(agent)}</StatCardValue>
              <StatCardLabel>Published Version</StatCardLabel>
            </StatCard>
          </StatsStrip>

          <Section>
            <SectionTitle>Agent Metadata</SectionTitle>
            <OverviewCard>
              <MetaRow>
                <MetaKey>Agent ID</MetaKey>
                <MonoVal>{agent.agentId.slice(0, 12)}…</MonoVal>
              </MetaRow>
              <MetaRow>
                <MetaKey>Type</MetaKey>
                <MetaVal>{TYPE_LABELS[agent.agentType] ?? agent.agentType}</MetaVal>
              </MetaRow>
              <MetaRow>
                <MetaKey>LLM Provider</MetaKey>
                <MetaVal>
                  {agent.config?.llmProvider ??
                    (agent.config as Record<string, unknown>)?.llmProvider as string ??
                    'OpenAI'}
                </MetaVal>
              </MetaRow>
              <MetaRow>
                <MetaKey>Model</MetaKey>
                <MetaVal>{agent.config?.llmModel ?? 'Not configured'}</MetaVal>
              </MetaRow>
              <MetaRow>
                <MetaKey>Temperature</MetaKey>
                <MetaVal>{agent.config?.temperature ?? '—'}</MetaVal>
              </MetaRow>
              <MetaRow>
                <MetaKey>Max Tokens</MetaKey>
                <MetaVal>{agent.config?.maxTokens ?? '—'}</MetaVal>
              </MetaRow>
              <MetaRow>
                <MetaKey>Visibility</MetaKey>
                <MetaVal>
                  <Badge
                    variant={agent.visibility === 'public' ? 'info' : 'neutral'}
                    size="sm"
                  >
                    {agent.visibility === 'public' ? 'Public' : 'Private'}
                  </Badge>
                </MetaVal>
              </MetaRow>
              <MetaRow>
                <MetaKey>Owner Org</MetaKey>
                <MonoVal>{agent.ownerOrgId ?? agent.organizationId ?? '—'}</MonoVal>
              </MetaRow>
              <MetaRow>
                <MetaKey>Created</MetaKey>
                <MetaVal>{new Date(agent.createdAt).toLocaleString()}</MetaVal>
              </MetaRow>
              <MetaRow>
                <MetaKey>Updated</MetaKey>
                <MetaVal>{new Date(agent.updatedAt).toLocaleString()}</MetaVal>
              </MetaRow>
            </OverviewCard>
          </Section>

          <Section>
            <SectionTitle>Description</SectionTitle>
            <p
              style={{
                fontSize: '0.9375rem',
                color: 'var(--text-secondary)',
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              {agent.description || 'No description provided.'}
            </p>
          </Section>

          {agent.config?.capabilities && agent.config.capabilities.length > 0 && (
            <Section>
              <SectionTitle>Capabilities</SectionTitle>
              <CapList>
                {agent.config.capabilities.map((cap) => (
                  <CapChip key={cap}>{cap}</CapChip>
                ))}
              </CapList>
            </Section>
          )}

          {knowledge && (
            <Section>
              <SectionTitle>Knowledge</SectionTitle>
              <OverviewCard>
                <KnowledgeRow>
                  <KnowledgeCount>{knowledge.documentCount}</KnowledgeCount>
                  documents connected
                </KnowledgeRow>
                <KnowledgeRow>
                  <KnowledgeCount>{knowledge.sourceCount}</KnowledgeCount>
                  knowledge sources
                  <Badge
                    variant={knowledge.status === 'synced' ? 'success' : 'warning'}
                    size="sm"
                  >
                    {knowledge.status}
                  </Badge>
                </KnowledgeRow>
              </OverviewCard>
            </Section>
          )}

          {hasPublishedVersion(agent) && (
            <Section>
              <SectionTitle>Integration</SectionTitle>
              <IntegrationCard>
                <IntegrationHeader
                  type="button"
                  onClick={() => setIntegrationExpanded((e) => !e)}
                  aria-expanded={integrationExpanded}
                >
                  <IntegrationSummary>
                    API endpoint, authentication, and request/response payloads
                  </IntegrationSummary>
                  <IntegrationChevron $expanded={integrationExpanded}>
                    &#x25B6;
                  </IntegrationChevron>
                </IntegrationHeader>
                {integrationExpanded && (
                  <IntegrationBody>
                    <IntegrationSubTitle>API Endpoint</IntegrationSubTitle>
                    <EndpointRow>
                      <MethodBadge>POST</MethodBadge>
                      <EndpointUrl>{CHAT_ENDPOINT}</EndpointUrl>
                      <CopyBtn
                        type="button"
                        onClick={() =>
                          copyToClipboard(CHAT_ENDPOINT, 'endpoint')
                        }
                        title="Copy URL"
                      >
                        {copyFeedback === 'endpoint' ? '\u2713' : '\uD83D\uDCCB'}
                      </CopyBtn>
                    </EndpointRow>

                    <IntegrationSubTitle>Authentication</IntegrationSubTitle>
                    <AuthRow>
                      <AuthItem>
                        <AuthLabel>Header</AuthLabel>
                        <AuthValueRow>
                          <AuthValue>{AUTH_HEADER}</AuthValue>
                          <CopyBtn
                            type="button"
                            onClick={() =>
                              copyToClipboard(AUTH_HEADER, 'auth-header')
                            }
                            title="Copy header"
                          >
                            {copyFeedback === 'auth-header' ? '\u2713' : '\uD83D\uDCCB'}
                          </CopyBtn>
                        </AuthValueRow>
                      </AuthItem>
                      <AuthItem>
                        <AuthLabel>API Key</AuthLabel>
                        <AuthValueRow>
                          <AuthValue>
                            x-api-key:{' '}
                            {env.API_KEY
                              ? `${env.API_KEY.slice(0, 12)}...`
                              : '&lt;API_KEY&gt;'}
                          </AuthValue>
                          <CopyBtn
                            type="button"
                            onClick={() =>
                              copyToClipboard(
                                API_KEY_HEADER(env.API_KEY),
                                'api-key',
                              )
                            }
                            title="Copy API key header"
                          >
                            {copyFeedback === 'api-key' ? '\u2713' : '\uD83D\uDCCB'}
                          </CopyBtn>
                        </AuthValueRow>
                      </AuthItem>
                    </AuthRow>

                    <IntegrationSubTitle>Request &amp; Response</IntegrationSubTitle>
                    <PayloadSection>
                      <PayloadHeader
                        type="button"
                        onClick={() => setRequestPayloadOpen((o) => !o)}
                      >
                        <PayloadTitle>Request Body</PayloadTitle>
                        <PayloadChevron $open={requestPayloadOpen}>&#x25B8;</PayloadChevron>
                      </PayloadHeader>
                      <PayloadBody $open={requestPayloadOpen}>
                        {buildRequestPayload(agent.agentId)}
                      </PayloadBody>
                    </PayloadSection>
                    <PayloadSection>
                      <PayloadHeader
                        type="button"
                        onClick={() => setResponsePayloadOpen((o) => !o)}
                      >
                        <PayloadTitle>Response Body</PayloadTitle>
                        <PayloadChevron $open={responsePayloadOpen}>&#x25B8;</PayloadChevron>
                      </PayloadHeader>
                      <PayloadBody $open={responsePayloadOpen}>
                        {buildResponsePayload(agent.agentName ?? agent.agentId)}
                      </PayloadBody>
                    </PayloadSection>
                    <ApiFieldNote>
                      Note: The API may use agent-name/query instead of
                      agent_id/message. Check the API docs for your deployment.
                    </ApiFieldNote>
                  </IntegrationBody>
                )}
              </IntegrationCard>
            </Section>
          )}
        </PanelScroll>
      </PanelLeft>

      <PanelRight>
        <PanelHeader>Fleet &amp; Versions</PanelHeader>
        <PanelScroll>
          <Section>
            <SectionTitle>Agent Fleet</SectionTitle>
            <FleetDiagram
              nodes={fleetNodes}
              edges={fleetEdges}
              height={360}
            />
          </Section>

          <Section>
            <SectionTitle>Version History</SectionTitle>
            <VersionTimeline versions={versions} />
          </Section>

          {agent.tags && agent.tags.length > 0 && (
            <Section>
              <SectionTitle>Tags</SectionTitle>
              <TagsWrap>
                {agent.tags.map((tag) => (
                  <Badge key={tag} variant="primary" size="sm">
                    {tag}
                  </Badge>
                ))}
              </TagsWrap>
            </Section>
          )}
        </PanelScroll>
      </PanelRight>
    </TwoPanel>
  );
};

OverviewTab.displayName = 'OverviewTab';
